import { NotFoundPage } from '~/components';
import {
  //Admin
  LoginAdmin,
  ForgotPasswordAdmin,
  ResetPasswordAdmin,
  SignUpAdmin,
  DashboardAdmin,
  JobAdmin,
  JobDetailAdmin,
  JobEstimateDetailAdmin,
  CRMAdmin,
  CRMDetailAdmin,
  CalendarAdmin,
  CommunicationAdmin,
  PaymentAdmin,
  ReportAdmin,
  SettingAdmin,

  //User
  HomeUser,
} from '~/pages';

import { IRouteModel } from '../interface/common';
import ViewInvoice from '~/layouts/admin/view-invoice/ViewInvoice';
import ClassicDetails from '~/pages/admin/reports/classic-details/ClassicDetails';

// ============================================
// Route Paths (Relative)
// ============================================

/**
 * Admin route paths (relative to /admin)
 * Use nested objects for module-based routes (e.g., job)
 */
export const adminRoute = {
  base: '/admin',

  // Authentication routes
  login: '/login',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  signUp: '/sign-up',

  // Main routes
  dashboard: '/dashboard',
  job: {
    main: '/job',
    detail: '/job/detail/:id',
    estimateCreate: '/job/detail/:id/estimate',
    estimateDetail: '/job/detail/:id/estimate/:estimateId',
    estimateEdit: '/job/detail/:id/estimate/:estimateId?edit'
  },
  crm: {
    main: '/crm',
    detail: '/crm/detail/:id',
  },
  calendar: '/calendar',
  communication: '/communication',
  payment: '/payment',
  report: '/report',
  reportClassicDetail: '/report/classic/:path',
  setting: '/setting',
};

/**
 * User route paths (relative to /)
 */
export const userRoute = {
  base: '/',
  home: '/home',
  viewInvoice: 'invoices/:id/view',
};

// ============================================
// Helper Functions
// ============================================

/**
 * Build absolute path from admin route
 * @param path - Relative path (e.g., '/job')
 * @returns Absolute path (e.g., '/admin/job')
 */
const buildAdminPath = (path: string): string => {
  return `${adminRoute.base}${path}`;
};

const buildUserPath = (path: string): string => {
  return `${userRoute.base}${path}`;
};

/**
 * Replace route parameters with actual values
 * @param path - Route path with params (e.g., '/job/detail/:id')
 * @param params - Object with param values (e.g., { id: '123' })
 * @returns Path with replaced params (e.g., '/job/detail/123')
 * 
 * @example
 * replaceParams('/job/detail/:id', { id: '123' }) // => '/job/detail/123'
 */
export const replaceParams = (
  path: string,
  params: Record<string, string | number>
): string => {
  let result = path;
  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`:${key}`, String(value));
  });
  return result;
};

/**
 * Build absolute admin route with optional params
 * @param route - Relative route path
 * @param params - Optional params to replace
 * @returns Absolute route path with replaced params
 * 
 * @example
 * getAdminRoute('/job/detail/:id', { id: '123' }) // => '/admin/job/detail/123'
 * getAdminRoute('/dashboard') // => '/admin/dashboard'
 */
export const getAdminRoute = (
  route: string,
  params?: Record<string, string | number>
): string => {
  const absolutePath = buildAdminPath(route);
  return params ? replaceParams(absolutePath, params) : absolutePath;
};

// ============================================
// Absolute Routes
// ============================================

/**
 * Absolute admin routes for navigation
 * Use these in navigate() calls throughout the application
 * 
 * @example
 * navigate(adminRouteAbsolute.dashboard)
 * navigate(adminRouteAbsolute.job.detail('123'))
 */
export const adminRouteAbsolute = {
  // Authentication routes
  login: buildAdminPath(adminRoute.login),
  forgotPassword: buildAdminPath(adminRoute.forgotPassword),
  resetPassword: buildAdminPath(adminRoute.resetPassword),
  signUp: buildAdminPath(adminRoute.signUp),

  // Main routes
  dashboard: buildAdminPath(adminRoute.dashboard),
  job: {
    main: buildAdminPath(adminRoute.job.main),

    detail: (id: string | number) =>
      getAdminRoute(adminRoute.job.detail, { id }),

    estimateCreate: (id: string | number) =>
      getAdminRoute(adminRoute.job.estimateCreate, { id }),
    estimateDetail: (id: string | number, estimateId: string | number) =>
      getAdminRoute(adminRoute.job.estimateDetail, { id, estimateId }),
    estimateEdit: (id: string | number, estimateId: string | number) =>
      getAdminRoute(adminRoute.job.estimateEdit, { id, estimateId }),
  },
  crm: {
    main: buildAdminPath(adminRoute.crm.main),
    detail: (id: string | number) =>
      getAdminRoute(adminRoute.crm.detail, { id }),
  },
  calendar: buildAdminPath(adminRoute.calendar),
  communication: buildAdminPath(adminRoute.communication),
  payment: buildAdminPath(adminRoute.payment),
  report: buildAdminPath(adminRoute.report),
  reportClassicDetail: (path: string) =>
    getAdminRoute(adminRoute.reportClassicDetail, { path }),
  setting: buildAdminPath(adminRoute.setting),
};

/**
 * Absolute user routes for navigation
 */
export const userRouteAbsolute = {
  home: userRoute.home,
};

// ============================================
// Route Configurations for React Router
// ============================================

/**
 * Public admin routes (accessible without authentication)
 * These routes are wrapped in Auth layout
 */
export const publicAdminRoutes: IRouteModel[] = [
  {
    path: '*',
    component: NotFoundPage,
  },
  {
    path: buildAdminPath(adminRoute.login),
    component: LoginAdmin,
  },
  {
    path: buildAdminPath(adminRoute.forgotPassword),
    component: ForgotPasswordAdmin,
  },
  {
    path: buildAdminPath(adminRoute.resetPassword),
    component: ResetPasswordAdmin,
  },
  {
    path: buildAdminPath(adminRoute.signUp),
    component: SignUpAdmin,
  },
];

/**
 * Private admin routes (require authentication)
 * These routes are wrapped in AdminMainLayout
 */
export const privateAdminRoutes: IRouteModel[] = [
  {
    path: buildAdminPath(adminRoute.dashboard),
    component: DashboardAdmin,
    name: 'Dashboard',
  },
  {
    path: buildAdminPath(adminRoute.job.main),
    component: JobAdmin,
    name: 'Job',
  },
  {
    path: buildAdminPath(adminRoute.job.detail),
    component: JobDetailAdmin,
    name: 'JobDetail',
  },

  {
    path: buildAdminPath(adminRoute.job.estimateCreate),
    component: JobEstimateDetailAdmin,
    name: 'JobEstimateDetail',
  },
  {
    path: buildAdminPath(adminRoute.job.estimateDetail),
    component: JobEstimateDetailAdmin,
    name: 'JobEstimateDetail',
  },


  {
    path: buildAdminPath(adminRoute.crm.main),
    component: CRMAdmin,
    name: 'CRM',
  },
  {
    path: buildAdminPath(adminRoute.crm.detail),
    component: CRMDetailAdmin,
    name: 'CRMDetail',
  },
  {
    path: buildAdminPath(adminRoute.calendar),
    component: CalendarAdmin,
    name: 'Calendar',
  },
  {
    path: buildAdminPath(adminRoute.communication),
    component: CommunicationAdmin,
    name: 'Communication',
  },
  {
    path: buildAdminPath(adminRoute.payment),
    component: PaymentAdmin,
    name: 'Payments',
  },
  {
    path: buildAdminPath(adminRoute.report),
    component: ReportAdmin,
    name: 'Report',
  },
  {
    path: buildAdminPath(adminRoute.setting),
    component: SettingAdmin,
    name: 'Setting',
  },
  {
    path: buildAdminPath(adminRoute.reportClassicDetail),
    component: ClassicDetails,
    name: 'ClassicDetails',
  },
];

/**
 * Public user routes
 */
export const publicUserRoutes: IRouteModel[] = [
  {
    path: buildUserPath(userRoute.viewInvoice),
    component: ViewInvoice,
    name: 'ViewInvoice',
  },
  {
    path: '*',
    component: NotFoundPage,
  },
];

/**
 * Private user routes
 */
export const privateUserRoutes: IRouteModel[] = [
  {
    path: userRoute.home,
    component: HomeUser,
  },
];

/**
 * Combined public routes (admin + user)
 */
export const publicRoutes = [...publicAdminRoutes, ...publicUserRoutes];

/**
 * Combined private routes (admin + user)
 * Currently empty, can be extended if needed
 */
export const privateRoutes: IRouteModel[] = [];